import { Row } from './row'
import { Table } from './table'

// TODO: add JSDoc examples

/**
 * Represents a sheet in an Excel workbook
 */
export class Sheet {
  public readonly name: string
  public readonly rows: Row[]

  private readonly _columnWidths: Partial<Record<number | string, number>>
  private _freezeRowCount: number | undefined

  constructor(
    name: string,
    // Can use a single row or a table, or a list of rows and/or tables
    ...content: readonly Maybe.ReadonlyArray<Row | Table<any>>[]
  ) {
    this.name = name
    this._columnWidths = {}
    const flatContent = content.flat()
    this.rows = flatContent.flatMap(rowOrTable =>
      rowOrTable instanceof Row ? rowOrTable : rowOrTable.rows,
    )

    // TODO: maybe get rid of the below, or come up with a better approach

    /**
     * If the sheet only contains a {@link Table}, this is that {@link Table}
     */
    const soleTable =
      flatContent.length === 1 && flatContent[0] instanceof Table
        ? flatContent[0]
        : undefined

    if (soleTable) {
      // Sets default widths for columns of table
      soleTable.headerRow.cells.forEach((cell, i) =>
        this.setColumnWidth(
          // 65 = A
          String.fromCharCode(65 + i),
          Table.DEFAULT_COLUMN_WIDTH,
        ),
      )
    }
  }

  public addRow(row: Row): this {
    this.rows.push(row)
    return this
  }

  /**
   * Sets the number of leading rows to keep frozen/visible when scrolling
   * (Excel freeze panes)
   */
  public freezeRows(count: number | undefined): this {
    this._freezeRowCount = count
    return this
  }

  public getFreezeRowCount(): number | undefined {
    return this._freezeRowCount
  }

  public addRows(rows: Row[]): this {
    this.rows.push(...rows)
    return this
  }

  public setColumnWidth(column: number | string, width: number): this {
    this._columnWidths[column] = width
    return this
  }

  public addColumnWidth(column: number | string, width: number): this {
    this._columnWidths[column] = (this._columnWidths[column] ?? 0) + width
    return this
  }

  public getColumnWidth(column: number | string): number | undefined {
    return this._columnWidths[column]
  }

  public *getAllColumnWidths(): Generator<{
    column: number | string
    width: number
  }> {
    for (const columnStr in this._columnWidths) {
      const width = this._columnWidths[columnStr]
      if (width !== undefined) {
        yield {
          // Records can only contain strings for keys, so columns that are
          // meant to be numbers must be converted back to numbers
          column: /\d+/.test(columnStr) ? Number(columnStr) : columnStr,
          width,
        }
      }
    }
  }

  public getTotalOfConfiguredColumnWidths(): number {
    let totalWidth = 0
    for (const { width } of this.getAllColumnWidths()) {
      totalWidth += width
    }
    return totalWidth
  }
}
